import React, { useState } from 'react'
import classNames from 'classnames'
import { toString } from 'lodash'
import {
  IconDeviceFloppy,
  IconChevronRight,
  IconCheck,
  IconX,
  IconHistory,
  IconEyeOff,
  IconArrowBackUp,
} from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { Pill } from '@/components/pills/Pill'
import { ExternalLink } from '@/components/ExternalLink'
import { CopyButton } from '@/components/buttons/CopyButton'
import { getDisplayLabel, isPassResult, isFailResult } from '@/utils/promptFeedback.helpers'
import { EditableDescription } from './EditableDescription'
import { TestResultDetails } from './TestResultDetails'
import { PromptDiffModal } from './PromptDiffModal'

/**
 * Individual test case row component
 * Displays a test case with expected/actual results, actions, and expandable details
 */
export const TestCaseRow = ({
  id,
  callId,
  isMessage,
  description,
  expectedResult,
  userFeedback,
  reasoning,
  result,
  isExpanded,
  setExpandedTests,
  testLoading,
  runAllLoading,
  onRunTest,
  onRemove,
  onDismiss,
  onRestore,
  isDismissed,
  isDismissing,
  isSelected,
  onToggleSelect,
  onEditTest,
  mustBePresent,
  isManual,
  isSavedPromptTest,
  isDeleting,
  onSaveToPrompt,
  isAlreadySaved,
  promptOutdated,
  oldPrompt,
  currentPrompt,
}) => {
  const [isSaving, setIsSaving] = useState(false)
  const [showPromptDiff, setShowPromptDiff] = useState(false)

  const handleSave = async (event) => {
    event.stopPropagation()
    if (isAlreadySaved || isSaving) {
      return
    }
    setIsSaving(true)
    try {
      await onSaveToPrompt()
    } finally {
      setIsSaving(false)
    }
  }

  const handleEditDescription = (newDescription) => {
    if (onEditTest) {
      onEditTest({ description: newDescription })
    }
  }

  const canEdit = !!onEditTest

  const handleToggle = () => {
    setExpandedTests((prev) => ({
      ...prev,
      [id]: !isExpanded,
    }))
  }

  return (
    <div
      className={classNames('test-case-row', {
        expanded: isExpanded,
        'test-case-row--dismissed': isDismissed,
      })}
    >
      <div className="test-case-row__header">
        <div className="test-case-row__trigger" onClick={handleToggle}>
          {onToggleSelect && (
            <span onClick={(event) => event.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                onChange={onToggleSelect}
                data-testid={`select-reported-call-${callId}`}
              />
            </span>
          )}
          <IconChevronRight size={14} className="test-chevron" />

          {callId && (
            <span className="test-case-row__call-id" onClick={(event) => event.stopPropagation()}>
              <ExternalLink
                url={isMessage ? `/messages/${callId}` : `/calls/${callId}`}
                label={callId}
              />
              <CopyButton content={toString(callId)} showTooltip iconOnly />
            </span>
          )}

          {promptOutdated && (
            <span onClick={(event) => event.stopPropagation()}>
              <Pill xs outlined caution clickable onClick={() => setShowPromptDiff(true)}>
                <IconHistory /> Prompt updated since feedback
              </Pill>
              <PromptDiffModal
                open={showPromptDiff}
                onClose={() => setShowPromptDiff(false)}
                oldPrompt={oldPrompt}
                newPrompt={currentPrompt}
              />
            </span>
          )}

          {isDismissed && (
            <Pill xs caution>
              Dismissed
            </Pill>
          )}
          {isManual && (
            <Pill xs informative>
              Manual
            </Pill>
          )}
          {isSavedPromptTest && (
            <Pill xs success>
              Saved
            </Pill>
          )}

          {getDisplayLabel(expectedResult) && (
            <span className="test-case-row__expected">
              <span>User expected:</span>
              <Pill
                xs
                success={isPassResult(expectedResult)}
                critical={isFailResult(expectedResult)}
              >
                {getDisplayLabel(expectedResult)}
              </Pill>
            </span>
          )}
        </div>

        <div className="test-case-row__actions" onClick={(event) => event.stopPropagation()}>
          {onRestore && (
            <Button
              small
              onClick={onRestore}
              loading={isDismissing}
              disabled={isDismissing}
              data-testid={`restore-reported-call-${callId}`}
            >
              <IconArrowBackUp /> Restore
            </Button>
          )}

          {onSaveToPrompt && !isAlreadySaved && (
            <Button
              square
              basic
              small
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving}
              tooltip="Save to prompt for ongoing regression testing"
              tooltipProps={{ placement: 'top' }}
            >
              <IconDeviceFloppy />
            </Button>
          )}

          {(isManual || isSavedPromptTest) && onRemove && (
            <Button small onClick={onRemove} loading={isDeleting} disabled={isDeleting}>
              Remove
            </Button>
          )}

          {onDismiss && (
            <Button
              square
              basic
              small
              onClick={onDismiss}
              loading={isDismissing}
              disabled={isDismissing}
              tooltip="Dismiss this call — it stops counting as feedback and the calibration agent will not use it"
              tooltipProps={{ placement: 'top' }}
              data-testid={`dismiss-reported-call-${callId}`}
            >
              <IconEyeOff />
            </Button>
          )}

          {result && (
            <Pill
              xs
              emphasized
              success={result.responses_match}
              critical={!result.responses_match}
              icon={result.responses_match ? <IconCheck /> : <IconX />}
            >
              {result.responses_match ? 'Pass' : 'Fail'}
            </Pill>
          )}

          {!isDismissed && (
            <Button
              small
              onClick={onRunTest}
              loading={testLoading[id] || runAllLoading}
              disabled={testLoading[id] || runAllLoading}
            >
              {result ? 'Test Again' : 'Test'}
            </Button>
          )}
        </div>
      </div>

      <div className={classNames('test-case-expandable-content', { expanded: isExpanded })}>
        <div className="test-case-content-inner">
          {(description || canEdit) && (
            <div className="test-case-parameters">
              <div className="test-parameter-row test-parameter-row--description">
                <EditableDescription
                  value={description}
                  onEdit={handleEditDescription}
                  editable={canEdit}
                />
              </div>
            </div>
          )}

          {userFeedback && (
            <div className="user-feedback-section">
              <div className="user-feedback-text">{userFeedback}</div>
            </div>
          )}

          {reasoning && (
            <div className="test-case-reasoning-section">
              <div className="test-case-reasoning-header">
                <span className="reasoning-name">Reasoning</span>
              </div>
              <div className="test-case-reasoning-text">{reasoning}</div>
            </div>
          )}

          {result && <TestResultDetails result={result} mustBePresent={mustBePresent} />}
        </div>
      </div>
    </div>
  )
}
